/*
  Poster interactive canvas effects.

  Loaded only when a poster-gallery block uses media type "interactive"
  with a designed effect (see the conditional load in poster-gallery.liquid).

  Each target is `<div data-ms-interactive="<effect>" ...><canvas></canvas></div>`.
  The runtime below owns the shared plumbing — DPR-aware sizing, a single
  RAF loop, pause-when-offscreen (IntersectionObserver), pointer wiring,
  resize, reduced-motion, and customizer section load/unload — and delegates
  the actual drawing to a per-effect controller.

  Effect factory contract:  factory(ctx, opts, getSize) -> controller
    opts    = { color, bg, stroke, charset, speed, reduced }
    getSize = () => ({ w, h })   // CSS pixels
    controller (all methods optional):
      static       true  → render one frame, don't run the RAF loop
      frame(t)           per-frame draw
      resize(w, h)       viewport changed
      pointermove(x,y)   pointer over the canvas (mouse + touch, CSS px)
      pointerleave()     pointer left
      pointerdown(x,y)   press start
      pointerup()        press end
      destroy()          teardown
*/
(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const EFFECTS = {
    'mouse-trail': mouseTrail,
    'draw-canvas': drawCanvas,
    'matrix-rain': matrixRain,
  };

  /* ---------- helpers ---------- */

  // "#rrggbb" -> {r,g,b}. Falls back to black on anything unexpected.
  function hexRgb(hex) {
    const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
    if (!m) return { r: 0, g: 0, b: 0 };
    const n = parseInt(m[1], 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
  }
  const rgba = (c, a) => `rgba(${c.r},${c.g},${c.b},${a})`;

  /* ---------- runtime ---------- */

  function initOne(el) {
    if (el.__msFxInited) return;
    const factory = EFFECTS[el.dataset.msInteractive];
    const canvas = el.querySelector('canvas');
    if (!factory || !canvas) return;
    el.__msFxInited = true;

    const ctx = canvas.getContext('2d');
    const opts = {
      color: el.dataset.color || '#7CFF6B',
      bg: el.dataset.bg || '#0b0b0b',
      stroke: parseFloat(el.dataset.stroke) || 6,
      charset: el.dataset.charset || 'katakana',
      speed: parseFloat(el.dataset.speed) || 5,
      reduced: REDUCED,
    };

    // Cap DPR at 2 — retina sharpness without quadratic fill cost on phones.
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let raf = null;
    let running = false;

    const getSize = () => ({ w, h });
    const controller = factory(ctx, opts, getSize);

    function size() {
      const rect = el.getBoundingClientRect();
      w = Math.max(1, Math.round(rect.width));
      h = Math.max(1, Math.round(rect.height));
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      if (controller.resize) controller.resize(w, h);
      if (controller.static && controller.frame) controller.frame(performance.now());
    }

    function loop(t) {
      if (!running) return;
      if (controller.frame) controller.frame(t);
      raf = requestAnimationFrame(loop);
    }
    function start() {
      if (running || controller.static) return;
      running = true;
      raf = requestAnimationFrame(loop);
    }
    function stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = null;
    }

    // Pointer events unify mouse + touch.
    const local = (e) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const onMove = (e) => { const p = local(e); controller.pointermove && controller.pointermove(p.x, p.y); };
    const onLeave = () => { controller.pointerleave && controller.pointerleave(); };
    const onDown = (e) => { const p = local(e); controller.pointerdown && controller.pointerdown(p.x, p.y); };
    const onUp = () => { controller.pointerup && controller.pointerup(); };
    if (controller.pointermove) el.addEventListener('pointermove', onMove);
    if (controller.pointerleave) el.addEventListener('pointerleave', onLeave);
    if (controller.pointerdown) el.addEventListener('pointerdown', onDown);
    if (controller.pointerup) { el.addEventListener('pointerup', onUp); el.addEventListener('pointercancel', onUp); }

    const ro = new ResizeObserver(() => size());
    ro.observe(el);

    // Only burn CPU while the poster is on screen.
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => { entry.isIntersecting ? start() : stop(); });
    }, { threshold: 0 });
    io.observe(el);

    size();

    el.__msFxDestroy = () => {
      stop();
      ro.disconnect();
      io.disconnect();
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      controller.destroy && controller.destroy();
      el.__msFxInited = false;
    };
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-ms-interactive]').forEach(initOne);
  }

  /* ============================================================
     Effect: mouse-trail — glowing particles follow the cursor.
     When idle (no real pointer for a moment) a virtual pointer
     drifts on a Lissajous path so the tile stays alive. Ambient
     drift is disabled under reduced-motion (pointer still works).
     ============================================================ */
  function mouseTrail(ctx, opts, getSize) {
    const col = hexRgb(opts.color);
    const bg = hexRgb(opts.bg);
    const particles = [];
    const MAX = 240;
    let px = 0;
    let py = 0;
    let haveReal = false;
    let lastReal = -9999;

    function spawn(x, y, n) {
      for (let i = 0; i < n; i++) {
        const a = Math.random() * Math.PI * 2;
        const s = Math.random() * 1.4;
        particles.push({
          x, y,
          vx: Math.cos(a) * s,
          vy: Math.sin(a) * s,
          life: 1,
          r: opts.stroke * (0.5 + Math.random() * 0.8),
        });
      }
      if (particles.length > MAX) particles.splice(0, particles.length - MAX);
    }

    return {
      pointermove(x, y) {
        haveReal = true;
        lastReal = performance.now();
        px = x; py = y;
        spawn(x, y, 3);
      },
      pointerleave() { haveReal = false; },
      frame(t) {
        const { w, h } = getSize();
        ctx.fillStyle = rgba(bg, 0.18);
        ctx.fillRect(0, 0, w, h);

        const idle = !haveReal || (t - lastReal > 1200);
        if (idle && !opts.reduced) {
          // Drift a virtual cursor so the effect self-animates at rest.
          px = w * (0.5 + 0.36 * Math.sin(t * 0.0007));
          py = h * (0.5 + 0.36 * Math.sin(t * 0.0011 + 1.3));
          spawn(px, py, 2);
        }

        ctx.globalCompositeOperation = 'lighter';
        for (let i = particles.length - 1; i >= 0; i--) {
          const p = particles[i];
          p.x += p.vx;
          p.y += p.vy;
          p.vx *= 0.96;
          p.vy *= 0.96;
          p.life -= 0.02;
          if (p.life <= 0) { particles.splice(i, 1); continue; }
          ctx.fillStyle = rgba(col, Math.max(0, p.life) * 0.9);
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.r * p.life, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.globalCompositeOperation = 'source-over';
      },
      destroy() { particles.length = 0; },
    };
  }

  /* ============================================================
     Effect: draw-canvas — "draw with light". Press + drag draws a
     glowing stroke that fades away shortly after (no persistence).
     ============================================================ */
  function drawCanvas(ctx, opts, getSize) {
    const col = hexRgb(opts.color);
    const bg = hexRgb(opts.bg);
    let drawing = false;
    let lastX = 0;
    let lastY = 0;

    function segment(x, y) {
      ctx.globalCompositeOperation = 'lighter';
      ctx.strokeStyle = rgba(col, 0.95);
      ctx.lineWidth = opts.stroke;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.shadowColor = rgba(col, 0.9);
      ctx.shadowBlur = opts.stroke * 1.5;
      ctx.beginPath();
      ctx.moveTo(lastX, lastY);
      ctx.lineTo(x, y);
      ctx.stroke();
      ctx.shadowBlur = 0;
      ctx.globalCompositeOperation = 'source-over';
      lastX = x; lastY = y;
    }

    return {
      pointerdown(x, y) { drawing = true; lastX = x; lastY = y; segment(x + 0.01, y + 0.01); },
      pointermove(x, y) { if (drawing) segment(x, y); },
      pointerup() { drawing = false; },
      pointerleave() { drawing = false; },
      frame() {
        const { w, h } = getSize();
        // Translucent wash fades strokes back into the background ("light").
        ctx.fillStyle = rgba(bg, 0.06);
        ctx.fillRect(0, 0, w, h);
      },
      resize() {
        const { w, h } = getSize();
        ctx.fillStyle = rgba(bg, 1);
        ctx.fillRect(0, 0, w, h);
      },
    };
  }

  /* ============================================================
     Effect: matrix-rain — falling glyph columns (parallax depth) with
     90s glitter. The cursor SHATTERS the rain: any glyph it touches is
     blown out into free-flying debris that bounces off the walls and
     floor, then once its bounce weakens drops off the bottom and is gone
     — it never rejoins the rain. Fresh columns keep the rain alive.
     Renders static under reduced-motion.
     ============================================================ */
  const CHARSETS = {
    katakana: 'アァカサタナハマヤラワガザダバパイィキシチニヒミリヰギジヂビピウゥクスツヌフムユルグズブヅプエ',
    latin: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
    digits: '0101010101230123456789',
    mixed: 'アカサ0123ABCDXYZ#@%$&*<>/',
  };
  // 90s-internet sugar mixed into the rain.
  const FX_EMOJI = ['✨', '🌟', '💫', '⭐', '🌈', '💖', '💕', '💿', '📼', '🕹️', '👾', '😎', '🌸', '💾', '🔮', '🦄', '💌', '🎀', '📟', '🌷'];
  const FX_STARS = ['✦', '✧', '⋆', '✩', '★', '☆', '✫', '✬', '❀', '✲'];
  // Tight, cohesive cute palette — pink, lavender, cyan, pale yellow.
  const FX_CUTE = [
    { r: 255, g: 110, b: 199 }, { r: 180, g: 120, b: 255 },
    { r: 125, g: 249, b: 255 }, { r: 255, g: 240, b: 150 },
  ];
  const FX_WHITE = { r: 255, g: 255, b: 255 };
  const pick = (arr) => arr[(Math.random() * arr.length) | 0];
  const brighten = (c, f) => ({ r: c.r + (255 - c.r) * f, g: c.g + (255 - c.g) * f, b: c.b + (255 - c.b) * f });

  function matrixRain(ctx, opts, getSize) {
    const baseCol = hexRgb(opts.color);
    const bg = hexRgb(opts.bg);
    const baseChars = CHARSETS[opts.charset] || CHARSETS.katakana;
    const REF_FONT = 22;                          // bigger glyphs
    const baseSpeed = 0.16 + opts.speed * 0.12;   // px/frame — slow
    const BLAST_R = 110;       // click / tap shockwave radius (wide knock-out)
    const BURST = 9;           // explosion speed when a glyph is freed
    const GRAV = 0.5;          // gravity on freed debris
    const WALL_REST = 0.6;     // wall / floor bounciness (restitution)
    const AIR = 0.995;         // air drag on debris
    const GROUND_FRICTION = 0.78;
    const MAX_DEBRIS = 500;    // cap — oldest pieces drop off beyond this
    const PARALLAX = 12;       // max depth-shift (px, at z=1) as the cursor moves

    let streams = [];
    let debris = [];
    let sparkles = [];
    let pointer = { x: -9999, y: -9999, active: false, blast: false };
    let par = { x: 0, y: 0 };  // eased parallax factors (-1..1), 0 when at rest

    const headGlyph = () => {
      const r = Math.random();
      if (r < 0.07) return pick(FX_EMOJI);
      if (r < 0.12) return pick(FX_STARS);
      return baseChars[(Math.random() * baseChars.length) | 0];
    };
    const trailGlyph = () => baseChars[(Math.random() * baseChars.length) | 0];
    // Cohesive stream tint: mostly the chosen hue, some white, some lighter.
    const streamCol = () => {
      const r = Math.random();
      if (r < 0.25) return FX_WHITE;
      if (r < 0.45) return brighten(baseCol, 0.45);
      return baseCol;
    };

    function newStream(w, h, atTop) {
      const z = 0.45 + Math.random() * 0.85;       // depth: 0.45 far .. 1.3 near
      const len = 4 + ((Math.random() * 4) | 0);   // head + 4–7 trail = 5–8 glyphs
      const font = Math.max(8, REF_FONT * z);
      const cells = [];
      for (let c = 0; c <= len; c++) {
        cells.push({ glyph: c === 0 ? headGlyph() : trailGlyph(), freed: false });
      }
      return {
        x: Math.random() * w,
        z, font, len, cells,
        v: baseSpeed * z * (0.7 + Math.random() * 0.6), // nearer falls faster
        y: atTop ? -font * 2 : Math.random() * h,
        col: streamCol(),
      };
    }

    function build() {
      const { w, h } = getSize();
      const n = Math.max(4, Math.round(w / (REF_FONT * 1.7))); // fewer, wider apart
      streams = [];
      for (let i = 0; i < n; i++) streams.push(newStream(w, h, false));
      streams.sort((a, b) => a.z - b.z); // far first, near drawn on top
    }

    // Blow a glyph out of the rain into free-flying debris that never returns.
    // baseX/baseY are the glyph's world position (parallax is re-applied at
    // render); visX/visY are where it visually sits, used for the blast angle.
    function freeCell(cell, baseX, baseY, visX, visY, font, colr, z, boost) {
      cell.freed = true;
      const dx = visX - pointer.x;
      const dy = visY - pointer.y;
      const d = Math.hypot(dx, dy) || 1;
      const ang = Math.atan2(dy, dx) + (Math.random() - 0.5); // ±0.5 rad spread
      const spd = BURST * (0.6 + Math.random()) * (boost || 1);
      debris.push({
        glyph: cell.glyph, x: baseX, y: baseY, z, font, col: colr,
        vx: Math.cos(ang) * spd,
        vy: Math.sin(ang) * spd - Math.random() * 3, // slight upward pop
        rot: 0, vr: (Math.random() - 0.5) * 0.4,     // tumble as it flies
        dropping: false,
      });
      if (debris.length > MAX_DEBRIS) debris.shift();
    }

    function draw(t, advance) {
      const { w, h } = getSize();
      // Higher wash alpha = shorter trail = crisper glyphs (less motion blur).
      ctx.fillStyle = rgba(bg, advance ? 0.24 : 1);
      ctx.fillRect(0, 0, w, h);
      ctx.textBaseline = 'top';

      // Parallax — ease a cursor-driven offset; near layers shift more than far,
      // so moving the mouse tilts the field through depth. Eases to 0 at rest.
      let ptx = 0, pty = 0;
      if (pointer.active) {
        ptx = (pointer.x - w / 2) / (w / 2);
        pty = (pointer.y - h / 2) / (h / 2);
      }
      par.x += (ptx - par.x) * 0.06;
      par.y += (pty - par.y) * 0.06;

      // ---- falling rain — glyphs the cursor touches leave for the debris system ----
      for (let i = 0; i < streams.length; i++) {
        const s = streams[i];
        const norm = (s.z - 0.45) / 0.85;           // 0 far .. 1 near
        const headA = 0.55 + 0.45 * norm;
        ctx.font = s.font + "px 'Geist Mono', ui-monospace, monospace";

        const pxz = -par.x * PARALLAX * s.z;        // depth-scaled parallax shift
        const pyz = -par.y * PARALLAX * s.z;
        for (let c = s.cells.length - 1; c >= 0; c--) {
          const cell = s.cells[c];
          if (cell.freed) continue;
          const wx = s.x;                           // world position
          const wy = s.y - c * s.font;
          const gx = wx + pxz;                      // on-screen (parallaxed)
          const gy = wy + pyz;
          // Hover only parallaxes — the rain shatters only on click/tap (blast).
          if (pointer.blast) {
            const dx = gx - pointer.x;
            const dy = gy - pointer.y;
            if (dx * dx + dy * dy < BLAST_R * BLAST_R) {
              freeCell(cell, wx, wy, gx, gy, s.font, s.col, s.z, 1.8);
              continue;
            }
          }
          if (c === 0) {
            ctx.fillStyle = rgba(brighten(s.col, 0.55), headA);
          } else {
            ctx.fillStyle = rgba(s.col, headA * (1 - c / (s.len + 1)) * 0.6);
          }
          ctx.fillText(cell.glyph, gx, gy);
        }

        if (advance) {
          const idx = (Math.random() * s.cells.length) | 0; // mutate a survivor
          if (Math.random() < 0.04 && !s.cells[idx].freed) {
            s.cells[idx].glyph = idx === 0 ? headGlyph() : trailGlyph();
          }
          s.y += s.v;
          // Respawn when the head's gone, the whole column's gone, or off-screen.
          const gone = s.cells[0].freed || s.cells.every((cl) => cl.freed);
          const offBottom = s.y - s.len * s.font > h;
          if (gone || (offBottom && Math.random() > 0.96)) streams[i] = newStream(w, h, true);
        }
      }

      // Click / tap shockwave is a one-frame event — clear it after the wide
      // knock-out above has been applied.
      if (pointer.blast) pointer.blast = false;

      // ---- debris — gravity + wall bounce; after a weak final bounce it drops off-screen ----
      for (let i = debris.length - 1; i >= 0; i--) {
        const d = debris[i];
        const pad = d.font * 0.5;
        if (advance) {
          d.vy += GRAV;
          d.vx *= AIR;
          d.x += d.vx;
          d.y += d.vy;
          d.rot += d.vr;
          if (d.x < pad) { d.x = pad; d.vx = Math.abs(d.vx) * WALL_REST; }
          else if (d.x > w - pad) { d.x = w - pad; d.vx = -Math.abs(d.vx) * WALL_REST; }
          if (d.y < pad) { d.y = pad; d.vy = Math.abs(d.vy) * WALL_REST; }
          else if (!d.dropping && d.y > h - pad) {
            // Bounce on the floor until the bounce goes weak, then stop colliding
            // and let it fall straight through and off the bottom of the screen.
            if (Math.abs(d.vy) < 2) {
              d.dropping = true;
            } else {
              d.y = h - pad;
              d.vy = -Math.abs(d.vy) * WALL_REST;
              d.vx *= GROUND_FRICTION;
              d.vr *= 0.8; // floor scuffs off some spin
            }
          }
          if (d.dropping && d.y - d.font > h) { debris.splice(i, 1); continue; }
        }
        const rx = d.x - par.x * PARALLAX * d.z;
        const ry = d.y - par.y * PARALLAX * d.z;
        ctx.save();
        ctx.translate(rx, ry);
        ctx.rotate(d.rot);
        ctx.font = d.font + "px 'Geist Mono', ui-monospace, monospace";
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = rgba(brighten(d.col, 0.5), 0.95);
        ctx.fillText(d.glyph, 0, 0);
        ctx.restore();
      }

      // ---- Glitter: white 4-point sparkles (varied size) + tiny colour dots ----
      // Smaller + sparser than before (lower cap, lower spawn rate).
      if (advance && sparkles.length < 32 && Math.random() < 0.45) {
        const isDot = Math.random() < 0.4;
        sparkles.push({
          x: Math.random() * w, y: Math.random() * h,
          size: isDot ? 1 + Math.random() * 1.5 : 2 + Math.random() * 5,
          col: isDot ? pick(FX_CUTE) : (Math.random() < 0.7 ? FX_WHITE : pick(FX_CUTE)),
          phase: Math.random() * Math.PI * 2,
          life: 1, dot: isDot,
        });
      }
      ctx.globalCompositeOperation = 'lighter';
      for (let i = sparkles.length - 1; i >= 0; i--) {
        const sp = sparkles[i];
        if (advance) sp.life -= 0.01;
        if (sp.life <= 0) { sparkles.splice(i, 1); continue; }
        const tw = 0.35 + 0.65 * Math.abs(Math.sin(t * 0.011 + sp.phase)); // flicker
        const a = sp.life * tw;
        if (sp.dot) {
          ctx.fillStyle = rgba(sp.col, a);
          ctx.beginPath(); ctx.arc(sp.x, sp.y, sp.size, 0, Math.PI * 2); ctx.fill();
        } else {
          const r = sp.size * (0.6 + 0.4 * tw);
          ctx.fillStyle = rgba(sp.col, a);
          ctx.beginPath(); // 4-point star
          ctx.moveTo(sp.x, sp.y - r); ctx.lineTo(sp.x + r * 0.2, sp.y);
          ctx.lineTo(sp.x, sp.y + r); ctx.lineTo(sp.x - r * 0.2, sp.y); ctx.closePath();
          ctx.moveTo(sp.x - r, sp.y); ctx.lineTo(sp.x, sp.y + r * 0.2);
          ctx.lineTo(sp.x + r, sp.y); ctx.lineTo(sp.x, sp.y - r * 0.2); ctx.closePath();
          ctx.fill();
          ctx.fillStyle = rgba(FX_WHITE, a); // bright core
          ctx.beginPath(); ctx.arc(sp.x, sp.y, Math.max(0.5, r * 0.12), 0, Math.PI * 2); ctx.fill();
        }
      }
      ctx.globalCompositeOperation = 'source-over';
    }

    return {
      static: opts.reduced,
      resize() { build(); if (opts.reduced) draw(0, false); },
      pointermove(x, y) { pointer.x = x; pointer.y = y; pointer.active = true; },
      pointerdown(x, y) { pointer.x = x; pointer.y = y; pointer.active = true; pointer.blast = true; },
      pointerleave() { pointer.active = false; },
      frame(t) { draw(t, !opts.reduced); },
      destroy() { streams = []; debris = []; sparkles = []; },
    };
  }

  /* ---------- boot + customizer hooks ---------- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-ms-interactive]').forEach((el) => {
      if (el.__msFxDestroy) el.__msFxDestroy();
    });
  });
})();
